import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Crosshair, LogOut, LayoutDashboard, PlusCircle, ClipboardList, Receipt, Phone, Activity, Timer } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const TITLE_MAP = {
    "/portal": ["Inicio", "Tu operación FindMed en tiempo real"],
    "/portal/request": ["Solicitar Ambulancia", "Captura de servicio"],
    "/portal/services": ["Mis Servicios", "Histórico y activos"],
    "/portal/invoices": ["Estado de Cuenta", "Facturas y saldos"],
    "/portal/sla": ["Cumplimiento SLA", "Tiempos de respuesta y traslado"],
};

const NAV_LINKS = [
    { to: "/portal", icon: LayoutDashboard, label: "Inicio" },
    { to: "/portal/request", icon: PlusCircle, label: "Solicitar Ambulancia" },
    { to: "/portal/services", icon: ClipboardList, label: "Mis Servicios" },
    { to: "/portal/sla", icon: Timer, label: "Cumplimiento SLA" },
    { to: "/portal/invoices", icon: Receipt, label: "Estado de Cuenta" },
];

export default function PortalLayout() {
    const { user, logout } = useAuth();
    const location = useLocation();
    const [clientInfo, setClientInfo] = useState(null);
    const [now, setNow] = useState(new Date());

    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/portal/me");
                if (data) setClientInfo(data);
            } catch (e) { console.warn(e); }
        })();
    }, []);

    let path = location.pathname;
    if (path.startsWith("/portal/services/")) path = "/portal/services";
    const [title, subtitle] = TITLE_MAP[path] || ["Portal Corporativo", "FindMed"];

    return (
        <div className="flex h-screen overflow-hidden bg-slate-100" data-testid="portal-layout">
            {/* Sidebar — distinto del operativo: más limpio, branding corporativo */}
            <aside className="w-64 bg-white border-r border-slate-200 flex flex-col flex-shrink-0">
                <div className="px-5 py-5 border-b border-slate-200 flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-900 flex items-center justify-center rounded-sm">
                        <Crosshair className="w-5 h-5 text-red-500" strokeWidth={2} />
                    </div>
                    <div>
                        <div className="font-display font-black text-base leading-none tracking-tight">FindMed</div>
                        <div className="text-[9px] text-red-600 uppercase tracking-widest mt-0.5 font-bold">Partner Portal</div>
                    </div>
                </div>

                {clientInfo?.client && (
                    <div className="px-5 py-4 border-b border-slate-200 bg-slate-50">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">Empresa</div>
                        <div className="text-sm font-semibold text-slate-900 leading-tight">{clientInfo.client.business_name}</div>
                        <div className="text-[10px] text-slate-500 mt-1 font-mono">{clientInfo.client.rfc || "—"}</div>
                        {clientInfo.client.has_agreement && (
                            <div className="mt-2 inline-flex fm-badge fm-badge-positive">Convenio activo</div>
                        )}
                    </div>
                )}

                <nav className="flex-1 py-3">
                    {NAV_LINKS.map((link) => {
                        const Icon = link.icon;
                        return (
                            <NavLink
                                key={link.to}
                                to={link.to}
                                end={link.to === "/portal"}
                                data-testid={`portal-nav-${link.to.split("/").pop() || "home"}`}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-5 py-2.5 text-sm transition-colors border-l-2 ${isActive ? "bg-slate-50 text-slate-900 border-red-600 font-semibold" : "text-slate-600 border-transparent hover:bg-slate-50 hover:text-slate-900"}`
                                }
                            >
                                <Icon className="w-4 h-4" strokeWidth={1.5} />
                                <span>{link.label}</span>
                            </NavLink>
                        );
                    })}
                </nav>

                <div className="px-4 py-3 border-t border-slate-200">
                    <div className="flex items-center gap-2 mb-3 px-1">
                        <Phone className="w-3.5 h-3.5 text-red-600" strokeWidth={1.5} />
                        <div>
                            <div className="text-[9px] uppercase tracking-widest font-bold text-slate-500">Línea Cabina 24/7</div>
                            <div className="font-mono text-xs font-bold text-slate-900">+52 55 2567 1884</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 bg-slate-100 rounded-sm flex items-center justify-center font-bold text-xs text-slate-700">
                            {(user?.name || "U").charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[12px] font-semibold text-slate-900 truncate">{user?.name}</div>
                            <div className="text-[10px] text-slate-500 truncate">{user?.email}</div>
                        </div>
                    </div>
                    <button
                        onClick={logout}
                        data-testid="portal-logout-btn"
                        className="w-full flex items-center justify-center gap-2 text-xs text-slate-600 hover:text-slate-900 border border-slate-200 hover:bg-slate-50 rounded-sm py-1.5 transition-colors"
                    >
                        <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />
                        Cerrar sesión
                    </button>
                </div>
            </aside>

            <div className="flex-1 flex flex-col min-w-0">
                <header className="h-14 bg-white border-b border-slate-200 flex items-center px-6 justify-between flex-shrink-0">
                    <div>
                        <h1 className="font-display font-bold text-base tracking-tight text-slate-900">{title}</h1>
                        {subtitle && <div className="text-[11px] text-slate-500 uppercase tracking-wider">{subtitle}</div>}
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-sm">
                            <Activity className="w-3 h-3 text-emerald-600" strokeWidth={2} />
                            <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">FindMed en línea</span>
                        </div>
                        <div className="font-mono text-xs text-slate-700 tabular-nums hidden md:block">
                            {now.toLocaleString("es-MX", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </div>
                    </div>
                </header>
                <main className="flex-1 overflow-auto bg-slate-100 p-5">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
